import type { FoundryV2JarvisEvaluationRecord } from "../api/foundryV2Client";

function statusTone(status: "ready" | "watch" | "blocked"): string {
  if (status === "ready") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "watch") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-red-200 bg-red-50 text-red-700";
}

function verdictTone(verdict: "promote" | "repair" | "hold"): string {
  if (verdict === "promote") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (verdict === "repair") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-red-200 bg-red-50 text-red-700";
}

type Props = {
  jarvisEvaluation: FoundryV2JarvisEvaluationRecord | null;
  isLoading: boolean;
  error: string | null;
};

export function FoundryJarvisEvaluationPanel({
  jarvisEvaluation,
  isLoading,
  error,
}: Props): JSX.Element {
  const report = jarvisEvaluation?.report ?? null;
  const preflight = jarvisEvaluation?.preflight ?? null;
  const artifactCoverage = report?.evidence.artifactCoverage ?? null;
  const coverageItems = artifactCoverage
    ? [
        ["runSummary", artifactCoverage.runSummary],
        ["policyReport", artifactCoverage.policyReport],
        ["metrics", artifactCoverage.metrics],
        ["executionCapsule", artifactCoverage.executionCapsule],
        ["toolCatalog", artifactCoverage.toolCatalog],
        ["judgeReport", artifactCoverage.judgeReport],
        ["repairLoopReport", artifactCoverage.repairLoopReport],
        ["evidenceContract", artifactCoverage.evidenceContract],
        ["evidencePackManifest", artifactCoverage.evidencePackManifest],
      ]
    : [];
  const coveredCount = coverageItems.filter(([, value]) => value).length;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f7fbff_100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-jarvis-evaluation-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
            Jarvis Evaluation v3
          </div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">
            Formal scoring of Jarvis against spec compliance, code quality, test coverage, security, debt, and replay discipline
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            This is the post-run truth surface. Foundry can no longer claim a pilot is green if Jarvis runtime evidence,
            repair posture, or governance artifacts are not materially closed.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !jarvisEvaluation ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading Jarvis evaluation...
        </div>
      ) : null}

      {report ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.1fr_0.85fr_0.85fr_0.9fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(report.summary.status)}`}>
                  {report.summary.status}
                </span>
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${verdictTone(report.summary.verdict)}`}>
                  {report.summary.verdict}
                </span>
                <span className="text-xs text-slate-500">Score {report.summary.score}/100</span>
              </div>
              <div className="mt-3 text-sm text-slate-700">{report.summary.headline}</div>
              <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {report.summary.blockerCount} blocker(s)
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {report.summary.watchCount} watch dimension(s)
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {coveredCount}/{coverageItems.length} artifacts covered
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {preflight?.verdict === "launch" ? "Jarvis launch posture accepted" : "Jarvis launch posture held"}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Founder view</div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {report.readableViews.founder.map((item) => (
                  <li key={item} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">{item}</li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Tactical view</div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {report.readableViews.tactical.map((item) => (
                  <li key={item} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">{item}</li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifacts</div>
              <div className="mt-3 space-y-2 text-xs">
                <a className="block font-medium text-blue-700 underline" href={jarvisEvaluation?.artifactUrls.evaluationReport ?? "#"}>
                  jarvis_evaluation_report.v1.json
                </a>
                {report.artifactUrls.runSummary ? (
                  <a className="block font-medium text-blue-700 underline" href={report.artifactUrls.runSummary}>
                    run_summary.json
                  </a>
                ) : null}
                {report.artifactUrls.judgeReport ? (
                  <a className="block font-medium text-blue-700 underline" href={report.artifactUrls.judgeReport}>
                    judge_report.json
                  </a>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Dimension grid</div>
                <div className="text-xs text-slate-500">
                  Run {jarvisEvaluation?.latestRunId ?? report.runId} · Work item {report.workItemId}
                </div>
              </div>
              <div className="mt-3 space-y-3">
                {report.dimensions.map((dimension) => (
                  <div key={dimension.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{dimension.label}</div>
                        <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          {dimension.id}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-600">{dimension.score}/100</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(dimension.status)}`}>
                          {dimension.status}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 h-2 rounded-full bg-slate-200">
                      <div
                        className={`h-2 rounded-full ${dimension.status === "ready" ? "bg-emerald-500" : dimension.status === "watch" ? "bg-amber-500" : "bg-red-500"}`}
                        style={{ width: `${Math.max(0, Math.min(100, dimension.score))}%` }}
                      />
                    </div>
                    <div className="mt-2 text-xs text-slate-600">{dimension.rationale}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Repair loop</div>
                <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                    Verdict: <span className="font-semibold text-slate-800">{report.repairLoop.verdict}</span>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                    Convergence: <span className="font-semibold text-slate-800">{report.repairLoop.convergenceScore}/100</span>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                    Iterations: <span className="font-semibold text-slate-800">{report.repairLoop.iterationsUsed}/{report.repairLoop.maxIterations}</span>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                    Guardrails: <span className="font-semibold text-slate-800">{report.repairLoop.guardrailViolationCount}</span>
                  </div>
                </div>
                {report.repairLoop.failureSignatures.length ? (
                  <div className="mt-3 space-y-2 text-[11px] text-slate-600">
                    {report.repairLoop.failureSignatures.map((signature) => (
                      <div key={signature} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                        {signature}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Governance posture</div>
                <div className="mt-3 space-y-3 text-xs text-slate-600">
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                    Policy verdict: <span className="font-semibold text-slate-800">{report.evidence.policyVerdict ?? "n/a"}</span>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                    Required approval: <span className="font-semibold text-slate-800">{report.evidence.requiredApproval ? "yes" : "no"}</span>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                    Stop reason: <span className="font-semibold text-slate-800">{report.evidence.stopReason ?? "n/a"}</span>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
                    Runtime:{" "}
                    <span className="font-semibold text-slate-800">
                      {report.modelRuntime.provider ?? "unknown"} / {report.modelRuntime.model ?? "unknown"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Next actions</div>
                <div className="mt-3 space-y-2 text-xs text-slate-600">
                  {report.nextActions.map((item) => (
                    <div key={item} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
